import { useQuery } from "@apollo/client";
import { ConfigurationKey, ConfigurationSchema } from "@salon/types";
import { useEffect, useRef } from "react";
import { GET_SALON_CONFIGURATIONS_BY_CATEGORY } from "@/graphql/configuration";
import { useSalonStore } from "@/lib/store/salon";
import { useConfigStore } from "@/lib/store/use-config-store";
import { useDebouncedConfigSubscription } from "./use-debounced-config-subscription";

/**
 * Integration hook that automatically loads and manages salon configurations
 *
 * This hook should be used at the app level to ensure configurations are
 * loaded when a salon is selected and kept in sync via subscriptions.
 *
 * Features:
 * - Automatic configuration loading when salon changes
 * - Real-time subscription management
 * - Error handling and recovery
 * - Loading state management
 *
 * @example
 * ```typescript
 * // In your app container or main layout component
 * function AppContainer() {
 *   useSalonConfigurationIntegration();
 *
 *   return <YourApp />;
 * }
 * ```
 */
export function useSalonConfigurationIntegration() {
  const { currentSalon } = useSalonStore();
  const salonId = currentSalon?.id;

  const setConfigurationsRef = useRef(useConfigStore.getState().setConfigurations);
  const setLoadingRef = useRef(useConfigStore.getState().setLoading);
  const setErrorRef = useRef(useConfigStore.getState().setError);

  // Update refs when store actions change
  useEffect(() => {
    const unsubscribe = useConfigStore.subscribe((state) => {
      setConfigurationsRef.current = state.setConfigurations;
      setLoadingRef.current = state.setLoading;
      setErrorRef.current = state.setError;
    });
    return unsubscribe;
  }, []);

  // Track if we're currently processing a subscription update to avoid conflicts
  const isProcessingSubscriptionUpdate = useRef(false);

  // Load all salon configurations when salon changes
  const { loading, error } = useQuery(GET_SALON_CONFIGURATIONS_BY_CATEGORY, {
    variables: { salonId: salonId ?? "" },
    skip: !salonId,
    fetchPolicy: "cache-and-network", // Always fetch fresh data
    onCompleted: (data) => {
      if (data?.getSalonConfigurationsByCategory?.categories && salonId) {
        // Transform all configurations from all categories
        const allConfigs: Partial<ConfigurationSchema> = {};

        data.getSalonConfigurationsByCategory.categories.forEach((categoryData) => {
          categoryData.configurations.forEach((config) => {
            const key = config.key as ConfigurationKey;

            // Parse value based on data type
            let parsedValue: any = config.value;

            switch (config.dataType) {
              case "boolean":
                parsedValue = config.value.toLowerCase() === "true";
                break;
              case "int":
                parsedValue = Number.parseInt(config.value, 10);
                break;
              case "string[]":
                parsedValue = config.value
                  .split(",")
                  .map((v) => v.trim())
                  .filter((v) => v.length > 0);
                break;
              default:
                parsedValue = config.value;
                break;
            }

            allConfigs[key] = parsedValue;
          });
        });

        // Update store with all configurations using refs
        setConfigurationsRef.current(salonId, allConfigs);
        setErrorRef.current(salonId, null);
      }
    },
    onError: (error) => {
      console.error("❌ Failed to load salon configurations:", error);
      if (salonId) {
        setErrorRef.current(salonId, error.message);
      }
    },
  });

  // Set up real-time subscription for all configuration changes with debouncing
  const { connected, isAuthenticationRequired } = useDebouncedConfigSubscription({
    salonId: salonId ?? "",
    enabled: !!salonId,
    debounceMs: 500, // Increased debounce for better stability
    onConfigurationChange: (_changedKeys) => {
      isProcessingSubscriptionUpdate.current = true;

      // Reset flag after processing
      setTimeout(() => {
        isProcessingSubscriptionUpdate.current = false;
      }, 200);
    },
  });

  // Update loading state with stable refs
  useEffect(() => {
    if (salonId) {
      setLoadingRef.current(salonId, loading);
    }
  }, [salonId, loading]);

  return {
    isLoading: loading,
    error: error?.message || null,
    isSubscriptionConnected: connected,
    isSubscriptionAvailable: !isAuthenticationRequired,
  };
}

/**
 * Hook to ensure configurations are initialized when the app starts
 *
 * This is a convenience hook that can be used in your app container
 * to automatically set up configuration management.
 */
export function useInitializeSalonConfigurations() {
  const integrationStatus = useSalonConfigurationIntegration();

  return integrationStatus;
}
